// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { isNullOrUndefined } from '@shared/utils';

type RelativeTimeUnit = Intl.RelativeTimeFormatUnit;

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_DAY = 86400;
const SECONDS_PER_WEEK = 604800;
const SECONDS_PER_MONTH = 2629800; // ~30.44 days
const SECONDS_PER_YEAR = 31557600; // ~365.25 days

function getUnitAndValue(diffSeconds: number): {
  value: number;
  unit: RelativeTimeUnit;
} {
  const abs = Math.abs(diffSeconds);
  const sign = diffSeconds < 0 ? -1 : 1;

  if (abs < SECONDS_PER_MINUTE) {
    return { value: sign * Math.round(abs), unit: 'second' };
  }
  if (abs < SECONDS_PER_HOUR) {
    return { value: sign * Math.round(abs / SECONDS_PER_MINUTE), unit: 'minute' };
  }
  if (abs < SECONDS_PER_DAY) {
    return { value: sign * Math.round(abs / SECONDS_PER_HOUR), unit: 'hour' };
  }
  if (abs < SECONDS_PER_WEEK) {
    return { value: sign * Math.round(abs / SECONDS_PER_DAY), unit: 'day' };
  }
  if (abs < SECONDS_PER_MONTH) {
    return { value: sign * Math.round(abs / SECONDS_PER_WEEK), unit: 'week' };
  }
  if (abs < SECONDS_PER_YEAR) {
    return { value: sign * Math.round(abs / SECONDS_PER_MONTH), unit: 'month' };
  }
  return { value: sign * Math.round(abs / SECONDS_PER_YEAR), unit: 'year' };
}

export interface FormatRelativeTimeOptions {
  /**
   * Locale for formatting (e.g. 'es', 'en-US'). Use i18n.language for
   * multi-language support.
   *
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/RelativeTimeFormat/RelativeTimeFormat#locales Intl.RelativeTimeFormat locales}
   */
  locale?: string;
  /** Base date to compare against. Defaults to now. */
  baseDate?: Date;
  /**
   * Formatting style. 'long' (e.g. "2 minutes ago"), 'short' (e.g. "2 min ago"),
   * 'narrow' (e.g. "2m ago").
   *
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/RelativeTimeFormat/RelativeTimeFormat#options Intl.RelativeTimeFormat options}
   */
  style?: 'long' | 'short' | 'narrow';
  /**
   * When 'auto', values like -1 day may become "yesterday"; when 'always', the
   * numeric value is always shown.
   *
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/RelativeTimeFormat/RelativeTimeFormat#numeric Intl.RelativeTimeFormat numeric}
   */
  numeric?: 'always' | 'auto';
  /**
   * When 'past', the formatted output is always in the past (e.g. "5 hours ago").
   * Use for "last updated" or "last sync" dates so the label never shows future
   * phrasing. When 'auto', the sign of the time difference is used (past or
   * future).
   */
  direction?: 'past' | 'auto';
}

/**
 * Formats a date as relative time using
 * {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/RelativeTimeFormat Intl.RelativeTimeFormat}
 * (e.g. "2 min ago", "in 3 hours"). Supports multiple languages via the locale
 * option (e.g. i18n.language from useTranslation()).
 *
 * @example // Past (es) formatRelativeTime(new Date(Date.now() - 120000), { locale:
 * 'es', style: 'short' }) // "hace 2 min"
 *
 * @example // Past (en-US) formatRelativeTime('2025-02-08T10:00:00Z', { locale:
 * 'en-US', style: 'short' }) // "1 day ago"
 *
 * @example // Always past (e.g. last sync) formatRelativeTime(lastSync, { locale:
 * i18n.language, style: 'short', direction: 'past' })
 *
 * @param date - Date to format (Date instance, ISO string, or timestamp in ms).
 * @param options - Locale, baseDate, style, numeric, direction.
 * @returns Formatted relative time string, or empty string if date is invalid.
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/RelativeTimeFormat Intl.RelativeTimeFormat - MDN}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/RelativeTimeFormat/format format() - MDN}
 */
export function formatRelativeTime(
  date: Date | string | number | null | undefined,
  options: FormatRelativeTimeOptions = {},
): string {
  if (isNullOrUndefined(date)) return '';
  const parsed =
    typeof date === 'object' && 'getTime' in date ? date : new Date(date);
  if (Number.isNaN(parsed.getTime())) return '';

  const baseDate = options.baseDate ?? new Date();
  const diffSeconds = (parsed.getTime() - baseDate.getTime()) / 1000;
  const { value: rawValue, unit } = getUnitAndValue(diffSeconds);
  const value =
    options.direction === 'past' && rawValue > 0 ? -Math.abs(rawValue) : rawValue;

  const locale = options.locale ?? undefined;
  const rtf = new Intl.RelativeTimeFormat(locale, {
    style: options.style ?? 'short',
    numeric: options.numeric ?? 'always',
  });
  return rtf.format(value, unit);
}
